import React, { useState, useEffect, useMemo } from "react";
import { useConfig } from "@peracto/peracto-config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxFull } from "@fortawesome/pro-regular-svg-icons/faBoxFull";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, connectHits, Configure, connectStats } from "react-instantsearch-dom";
import dayjs from "dayjs";

import isEmpty from "lodash/isEmpty";
import cn from "classnames";

const formatPrice = (price) => {
  if (!price) return "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(price / 100);
};

const ProductFlash = ({ children, bgClass, hexColour }) => {
  const style = hexColour ? { backgroundColor: hexColour } : {};
  return (
    <span
      className={cn(
        "absolute right-0 top-0 mt-2 p-2 text-sm uppercase leading-4 tracking-wider text-brand-white",
        bgClass,
      )}
      style={style}
    >
      {children}
    </span>
  );
};

const useWindowSize = () => {
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 0;
  });

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowWidth;
};

let Icon = () => (
  <div className="flex w-full items-center justify-center py-10">
    <FontAwesomeIcon icon={faBoxFull} size="6x" />
  </div>
);

const ProductCard = ({
  isEditMode,
  name,
  slug,
  prices,
  image,
  availableVariants,
  shippingServiceType,
  salesUnit,
  createdAt,
  sashText,
  sashColour,
}) => {
  const productHref = `/product/${slug}`;

  const price = prices?.price?.incTax;
  const isOnSale = (prices?.salePrice?.incTax || 0) > 0;
  const isClearance = (prices?.clearancePrice?.incTax || 0) > 0;
  const isNew = createdAt && dayjs().diff(createdAt * 1000, "days") < 30;

  return (
    <div data-testid="product-card" className="flex h-full w-full flex-col justify-between">
      <div className="flex h-full flex-grow basis-auto flex-col">
        <a
          data-testid="product-card__link"
          href={productHref}
          className="mb-[10px] flex flex-1 flex-col gap-y-[10px]"
          onClick={(e) => {
            if (isEditMode) {
              e.preventDefault();
            }
          }}
        >
          <div className="relative">
            {sashText && sashText.length > 0 ? (
              <ProductFlash hexColour={sashColour || "#FFF"}>
                {sashText.length > 20 ? sashText.slice(0, 18) + ".." : sashText}
              </ProductFlash>
            ) : (
              <>
                {isOnSale && !isClearance && <ProductFlash bgClass="bg-brand-red">Sale</ProductFlash>}
                {isClearance && <ProductFlash bgClass="bg-brand-red">Clearance</ProductFlash>}
                {isNew && !isOnSale && !isClearance && <ProductFlash bgClass="bg-brand-yellow">New</ProductFlash>}
              </>
            )}

            <img
              className="aspect-square w-full rounded"
              data-testid="product-card__image"
              src={image || "/images/placeholder.png"}
              alt={name}
              width={310}
              height={310}
            />
          </div>
        </a>

        <div className="mb-[10px] flex flex-1 flex-grow flex-col gap-y-[10px]">
          <div className="flex flex-grow flex-col gap-y-[10px] p-3">
            <h5 data-testid="product-card__name" className="block">
              {name}
            </h5>

            {price && (
              <div className="flex items-center gap-x-[10px] mt-auto">
                <span data-testid="product-card__now-price" className="text-2xl font-bold text-brand-dark-grey">
                  {availableVariants ? "From " : ""}
                  {formatPrice(price)}
                </span>
                <span className="text-sm text-brand-dark-grey">
                  {!salesUnit || salesUnit?.toLowerCase() === "each"
                    ? "each"
                    : `per ${salesUnit?.replaceAll("_", " ")}`}
                </span>
                <div
                  data-testid="tax-message"
                  className="flex flex-col text-[10px] font-normal leading-[11px] text-brand-black"
                >
                  <span className="uppercase">Inc.</span>
                  <span>VAT</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-[5px]">
        <>
          <button
            data-testid="product-card__add-to-basket"
            className="flex w-full items-center justify-center gap-x-2 rounded bg-brand-dark-green px-4 py-2 text-sm font-bold uppercase text-white hover:opacity-90"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            Delivery
          </button>

          {shippingServiceType === "delivery" && (
            <button
              data-testid="product-card__click-collect"
              className="flex w-full items-center justify-center gap-x-2 rounded bg-brand-dark-green px-4 py-2 text-sm font-bold uppercase text-white hover:opacity-90"
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              Click & Collect
            </button>
          )}
        </>

        <button
          data-testid="product-card__view-product"
          className="flex w-full items-center justify-center rounded border border-brand-primary px-4 py-2 text-sm font-bold uppercase text-brand-primary no-underline hover:bg-brand-primary hover:text-white hover:no-underline"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          View Product
        </button>
      </div>
    </div>
  );
};

const Hits = (props) => {
  const { hits, isEditMode, numberOfProducts, currentPage, PRODUCTS_PER_PAGE } = props;

  let productsToRender = hits;

  if (numberOfProducts !== undefined && numberOfProducts > 0) {
    const productsAlreadyRendered = currentPage * PRODUCTS_PER_PAGE;

    const productsRemaining = numberOfProducts - productsAlreadyRendered;

    if (productsRemaining < hits.length) {
      productsToRender = hits.slice(0, productsRemaining);
    }
  }

  const inStorefront = !window?.location?.pathname.includes("/content/edit/");

  return productsToRender.length > 0 ? (
    <>
      {productsToRender.map((hit, index) => {
        const name = hit?.attributes?.product_name;
        const slug = hit?.slug;
        const availableVariants = hit?.variants;

        let prices = {};
        if (availableVariants === true) {
          prices = {
            wasPrice: hit?.fromPrices?.rrp
              ? { incTax: hit?.fromPrices?.rrp?.inc, excTax: hit?.fromPrices?.rrp?.exc }
              : null,
            price: hit?.fromPrices?.price
              ? { incTax: hit?.fromPrices?.price?.inc, excTax: hit?.fromPrices?.price?.exc }
              : null,
            salePrice: hit?.fromPrices?.salePrice
              ? { incTax: hit?.fromPrices?.salePrice?.inc, excTax: hit?.fromPrices?.salePrice?.exc }
              : null,
            clearancePrice: hit?.fromPrices?.clearancePrice
              ? { incTax: hit?.fromPrices?.clearancePrice?.inc, excTax: hit?.fromPrices?.clearancePrice?.exc }
              : null,
          };
        } else {
          prices = {
            wasPrice: hit?.prices?.rrp ? { incTax: hit?.prices?.rrp?.inc, excTax: hit?.prices?.rrp?.exc } : null,
            price: hit?.prices?.price ? { incTax: hit?.prices?.price?.inc, excTax: hit?.prices?.price?.exc } : null,
            salePrice: hit?.prices?.salePrice
              ? { incTax: hit?.prices?.salePrice?.inc, excTax: hit?.prices?.salePrice?.exc }
              : null,
            clearancePrice: hit?.prices?.clearancePrice
              ? { incTax: hit?.prices?.clearancePrice?.inc, excTax: hit?.prices?.clearancePrice?.exc }
              : null,
          };
        }

        const image = hit?.resources?.find((resource) => resource?.type === "image")?.location;
        const shippingServiceType = hit?.shippingServiceType;
        const salesUnit = hit?.salesUnit;
        const createdAt = hit?.createdAt;
        const sashText = hit?.attributes?.sash_text;
        const sashColour = hit?.attributes?.sash_colour;

        return (
          <ProductCard
            key={hit?.slug || `hit_${index}`}
            isEditMode={isEditMode}
            name={name}
            slug={slug}
            prices={prices}
            image={image}
            availableVariants={availableVariants}
            shippingServiceType={shippingServiceType}
            salesUnit={salesUnit}
            createdAt={createdAt}
            sashText={sashText}
            sashColour={sashColour}
          />
        );
      })}
    </>
  ) : isEditMode || !inStorefront ? (
    <Icon />
  ) : null;
};
const CustomHits = connectHits(Hits);

const StatsUpdater = ({ nbHits, onStatsUpdate }) => {
  useEffect(() => {
    if (onStatsUpdate) {
      onStatsUpdate({ nbHits });
    }
  }, [nbHits, onStatsUpdate]);

  return null;
};
const ConnectedStatsUpdater = connectStats(StatsUpdater);

const ProductsContent = ({
  numberOfProducts,
  attributes = [],
  attributeOperator = "and",
  border,
  isEditMode,
  currentPage,
  PRODUCTS_PER_PAGE,
  onStatsUpdate,
}) => {
  const config = useConfig();
  const algolia = config.get("algolia") || {};

  const { applicationId, searchApiKey, indexName } = algolia;

  const searchClient = algoliasearch(applicationId, searchApiKey);

  const [facetFilters, setFacetFilters] = useState();

  useEffect(() => {
    if (attributes?.length > 0) {
      const categories = attributes?.reduce((acc, { attribute, value }) => {
        // Attribute is already saved with prefix (e.g. 'attributes.brand' or 'sku')
        // so we can use it directly
        if (value.length > 0) acc.push(`${attribute}:${value}`);

        return acc;
      }, []);

      setFacetFilters(categories);
    }
  }, [attributes]);

  if (isEmpty(algolia)) {
    console.error("No Algolia configuration defined. Please refer to the Peracto documentation.");
  }

  return useMemo(
    () =>
      isEmpty(algolia) ? null : (
        <InstantSearch searchClient={searchClient} indexName={indexName}>
          <Configure
            hitsPerPage={PRODUCTS_PER_PAGE}
            page={currentPage}
            facetFilters={facetFilters ? (attributeOperator === "and" ? facetFilters : [facetFilters]) : null}
          />

          <ConnectedStatsUpdater onStatsUpdate={onStatsUpdate} />

          <CustomHits
            isEditMode={isEditMode}
            border={border}
            numberOfProducts={numberOfProducts}
            currentPage={currentPage}
            PRODUCTS_PER_PAGE={PRODUCTS_PER_PAGE}
          />
        </InstantSearch>
      ),
    [
      algolia,
      searchClient,
      indexName,
      border,
      facetFilters,
      attributeOperator,
      isEditMode,
      PRODUCTS_PER_PAGE,
      currentPage,
      onStatsUpdate,
      numberOfProducts,
    ],
  );
};

const ProductsRenderer = ({ state, ...props }) => {
  const { numberOfProducts } = state;

  const windowWidth = useWindowSize();

  const TABLET_BREAKPOINT = 768;
  const DESKTOP_BREAKPOINT = 992;

  const PRODUCTS_PER_PAGE = useMemo(() => {
    if (windowWidth === 0) {
      return 1;
    }
    if (windowWidth < TABLET_BREAKPOINT) {
      return 1;
    }
    if (windowWidth < DESKTOP_BREAKPOINT) {
      return 2;
    }
    return 4;
  }, [windowWidth]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const isAtStart = currentPage === 0;
  const isAtEnd = currentPage + 1 >= totalPages;

  const handlePageChange = (direction) => {
    if (direction === "left" && !isAtStart) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else if (direction === "right" && !isAtEnd) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handleStatsUpdate = ({ nbHits }) => {
    let effectiveHits = nbHits;
    if (numberOfProducts !== undefined && nbHits > numberOfProducts) {
      effectiveHits = numberOfProducts;
    }

    const newTotalPages = Math.ceil(effectiveHits / PRODUCTS_PER_PAGE);
    setTotalPages(newTotalPages > 0 ? newTotalPages : 1);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [PRODUCTS_PER_PAGE]);

  return (
    <div className="pl-[15px]">
      <div className="mb-[15px] flex flex-col items-center justify-between md:flex-row">
        <div className="ml-auto hidden items-center gap-[10px] md:flex">
          <NavButton direction="left" disabled={isAtStart} onClick={() => handlePageChange("left")} />
          <NavButton direction="right" disabled={isAtEnd} onClick={() => handlePageChange("right")} />
        </div>
      </div>
      <div className="product-card-container content__products flex flex-row flex-wrap justify-center gap-[20px] md:justify-start">
        <div className="grid grid-cols-1 gap-[15px] md:grid-cols-2 lg:grid-cols-4 lg:gap-[30px]">
          <ProductsContent
            {...state}
            {...props}
            currentPage={currentPage}
            PRODUCTS_PER_PAGE={PRODUCTS_PER_PAGE}
            onStatsUpdate={handleStatsUpdate}
          />
        </div>
      </div>

      <div className="mt-[15px] flex items-center justify-center gap-[10px] md:hidden">
        <NavButton direction="left" disabled={isAtStart} onClick={() => handlePageChange("left")} />
        <NavButton direction="right" disabled={isAtEnd} onClick={() => handlePageChange("right")} />
      </div>
    </div>
  );
};

const NavButton = ({ direction, disabled, onClick }) => (
  <button
    className={cn(
      `h-[50px] w-[50px] rounded-full bg-brand-light-grey text-[20px] leading-[26px] text-brand-black hover:bg-brand-light-grey active:bg-brand-grey`,
      { "cursor-not-allowed opacity-25": disabled },
    )}
    onClick={onClick}
    disabled={disabled}
  >
    <FontAwesomeIcon icon={direction === "left" ? faChevronLeft : faChevronRight} />
  </button>
);

export default ProductsRenderer;
